import React, { useState, useEffect, memo, useCallback, useMemo } from 'react';
import { Box, CssBaseline, AppBar, Toolbar, IconButton, Typography, useTheme, useMediaQuery } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from './Sidebar';
import { UserRole } from '../../types/user';

interface DashboardLayoutProps {
  children: React.ReactNode;
  title?: string;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = memo(({ children, title }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const { user, logout } = useAuth();

  // 現在のパスからタイトルを取得する
  const pageTitle = useMemo((): string => {
    if (title) return title;
    
    // パスに基づいてタイトルを設定
    switch (location.pathname) {
      case '/':
        return user?.role === UserRole.ADMIN ? '管理者ダッシュボード' : 'ダッシュボード';
      case '/orders':
        return 'オーダー管理';
      case '/users':
        return 'ユーザー管理';
      case '/settings':
        return 'システム設定';
      case '/shifts':
        return 'シフト管理';
      case '/gps-tracking':
        return 'GPS追跡';
      case '/reports':
        return 'レポート';
      default:
        return '配送管理システム';
    }
  }, [location.pathname, title, user?.role]);

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen(prevState => !prevState);
  }, []);

  const handleLogout = useCallback(() => {
    // AuthContext.logout() を使用する。
    // 従来はここで localStorage/sessionStorage の token/user だけを個別に削除していたが、
    // AUTH_INIT_COMPLETE や LAST_AUTH_REFRESH 等の関連フラグ、Axiosのデフォルトヘッダー、
    // AuthContext内の isAuthenticated/user state がクリアされず、
    // ログアウト後も認証状態が残ってしまう不整合があった。
    logout();
  }, [logout]);

  const drawerWidth = useMemo(() => isMobile ? 240 : 280, [isMobile]);

  // サイドバーコンポーネントをメモ化
  const sidebarComponent = useMemo(() => (
    <Sidebar
      mobileOpen={mobileOpen}
      onDrawerToggle={handleDrawerToggle}
      onLogout={handleLogout}
    />
  ), [mobileOpen, handleDrawerToggle, handleLogout]);

  // メインコンテンツのスタイルをメモ化
  const mainBoxSx = useMemo(() => ({
    flexGrow: 1,
    // flexアイテムは子要素(横に広いテーブル等)があると、デフォルトのmin-width:autoの性質で
    // 親の幅を超えて広がってしまう。minWidthを0にし、overflowXで横スクロールを本要素内に閉じ込める。
    minWidth: 0,
    overflowX: 'hidden',
    p: 3,
    width: { sm: `calc(100% - ${drawerWidth}px)` },
    mt: '64px',
    minHeight: '100vh',
    backgroundColor: theme.palette.background.default
  }), [drawerWidth, theme.palette.background.default]);

  // アプリバーのスタイルをメモ化
  const appBarSx = useMemo(() => ({
    width: { sm: `calc(100% - ${drawerWidth}px)` },
    ml: { sm: `${drawerWidth}px` },
  }), [drawerWidth]);

  // ナビゲーションのスタイルをメモ化
  const navBoxSx = useMemo(() => ({ 
    width: { sm: drawerWidth }, 
    flexShrink: { sm: 0 } 
  }), [drawerWidth]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={appBarSx}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {pageTitle}
          </Typography>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={navBoxSx}
      >
        {sidebarComponent}
      </Box>
      <Box
        component="main"
        sx={mainBoxSx}
      >
        {children}
      </Box>
    </Box>
  );
});

export default DashboardLayout;
